import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  InfiniteData,
  QueryKey,
  QueryFunction,
} from '@tanstack/react-query';

// Define the extra props we want to inject

export default function useDebounceInfiniteQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: UseInfiniteQueryOptions<
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TPageParam
  > & {
    queryFn: QueryFunction<TQueryFnData, TQueryKey, TPageParam>;
    debounceTime?: number;
  },
) {
  const { debounceTime = 300, queryFn, ...rest } = options;

  return useInfiniteQuery({
    ...rest,
    queryFn: async (context) => {
      // Create a signal that reacts to React Query's internal cancellation
      const { signal } = context;

      return new Promise<TQueryFnData>((resolve, reject) => {
        const timeoutId = setTimeout(async () => {
          try {
            // Check if it was canceled during the wait
            if (signal.aborted) return reject(signal.reason);

            // Execute the original queryFn
            const result = await queryFn(context);
            resolve(result);
          } catch (err) {
            reject(err);
          }
        }, debounceTime);

        // Link React Query's cancellation to our timeout
        signal.addEventListener('abort', () => {
          clearTimeout(timeoutId);
        });
      });
    },
  });
}
